export interface RelativeTimeOptions {
  granularity?: 'seconds' | 'minutes' | 'hours'
  nowText?: string
  nowThreshold?: number
}

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

export function parseInputDate(value: string | Date): Date {
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate())
  }

  const match = DATE_ONLY_PATTERN.exec(value)
  if (match) {
    const [, year, month, day] = match
    return new Date(Number(year), Number(month) - 1, Number(day))
  }

  return new Date(value)
}

export function formatRelativeTime(
  timestamp: string | Date,
  options: RelativeTimeOptions = {},
): string {
  const {
    granularity = 'minutes',
    nowText = 'Just now',
    nowThreshold = granularity === 'seconds' ? 5 : 1,
  } = options

  if (!timestamp) return nowText

  const now = new Date()
  const time = timestamp instanceof Date ? timestamp : new Date(timestamp)
  const diffInMs = now.getTime() - time.getTime()

  if (granularity === 'seconds') {
    const diffInSeconds = Math.floor(diffInMs / 1000)

    if (diffInSeconds < nowThreshold) return nowText
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
    return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  if (granularity === 'hours') {
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))

    if (diffInHours < 1) return nowText
    if (diffInHours < 24) return `${diffInHours}h ago`
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`
    return time.toLocaleDateString()
  }

  const diffInMinutes = Math.floor(diffInMs / (1000 * 60))

  if (diffInMinutes < nowThreshold) return nowText
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`
  return time.toLocaleDateString()
}

export function formatShortDate(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value)
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export function formatInputDate(value: string | Date): string {
  const date = parseInputDate(value)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

export function formatLongDate(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value)
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function formatShortTime(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value)
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export function formatDateTime(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value)
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function isSameDay(date1: string | Date, date2: string | Date): boolean {
  const d1 = date1 instanceof Date ? date1 : new Date(date1)
  const d2 = date2 instanceof Date ? date2 : new Date(date2)

  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  )
}
